import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';

export type CatalogBarcodes = { upc?: string; ean?: string; gtin?: string };
export type BarcodeStoreAction = 'notify' | 'push';

export type AffectedListing = {
  mappingDocId: string;
  organizationId: string;
  organizationName: string | null;
  integrationId: string;
  integrationName: string | null;
  platform: string;
  platformProductId: string;
  state: 'carries_old' | 'unconfirmed' | 'already_new' | 'different' | 'store_own';
  evidence: 'live' | 'recorded' | 'store_mapping' | 'none';
  listingBarcode: string | null;
  fixability: string;
  fixMessage: string;
};

export type BarcodeUseRow = {
  collection: 'canonicalProducts' | 'stagedProducts';
  id: string;
  name: string;
  sku: string;
  field: string;
  value: string;
  status: string;
  uses: 'new' | 'old';
};

export type BarcodeOverrideCounts = {
  stores: number;
  carryingOld: number;
  unconfirmed: number;
  writable: number;
  cannotWrite: number;
  storeOwn: number;
  alreadyNew: number;
  different: number;
};

export type BarcodeOverridePreview = {
  stagedId: string;
  canonicalId: string;
  productName: string;
  before: CatalogBarcodes;
  after: CatalogBarcodes;
  clearedFields: string[];
  replacedBarcodes: string[];
  newBarcode: string;
  block: string | null;
  rejects: Array<{ value: string; reason: string }>;
  uses: BarcodeUseRow[];
  listings: AffectedListing[];
  counts: BarcodeOverrideCounts;
};

export type StoreActionSummary = {
  action: BarcodeStoreAction;
  storesNotified: number;
  storesSkippedFlagOff: number;
  pushed: number;
  notCarrying: number;
  failed: number;
  remaining: number;
  manual: number;
};

export type BarcodeOverrideResult = {
  overrideId: string;
  canonicalId: string;
  before: CatalogBarcodes;
  after: CatalogBarcodes;
  counts: BarcodeOverrideCounts;
  legacyOverridesCarried: number;
  store: StoreActionSummary | null;
  uses: BarcodeUseRow[];
};

export type BarcodeCorrectionRow = {
  id: string;
  organizationId: string;
  integrationName: string | null;
  platform: string;
  status: string;
  fixability: string;
  lastError?: string | null;
};

export type BarcodeOverrideRecord = {
  id: string;
  newBarcode: string;
  replacedBarcodes: string[];
  storeAction: BarcodeStoreAction | null;
  corrections: BarcodeCorrectionRow[];
};

/** The API's error handler answers `{ error: { message } }`; a route may answer `{ error: 'text' }`. */
export function apiErrorMessage(json: unknown, status: number): string {
  const body = (json || {}) as { error?: unknown; message?: unknown };
  const nested = typeof body.error === 'object' && body.error ? (body.error as { message?: unknown }).message : body.error;
  const message = nested || body.message;
  return typeof message === 'string' && message ? message : `Request failed (${status})`;
}

async function call<T>(path: string, init?: { method: string; body?: unknown }): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await makeApiCall(path, {
    method: init?.method || 'GET',
    headers: init ? { ...headers, 'Content-Type': 'application/json' } : headers,
    ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(apiErrorMessage(json, res.status));
  return json as T;
}

export const barcodeOverrideApi = {
  enabled: async () => (await call<{ data: { enabled: boolean } }>('/staged-products/barcode-override-enabled')).data.enabled === true,
  preview: async (stagedId: string) =>
    (await call<{ data: BarcodeOverridePreview }>(`/staged-products/${stagedId}/barcode-override/preview`)).data,
  apply: async (stagedId: string, expectedBefore: CatalogBarcodes, expectedAfter: CatalogBarcodes, storeAction: BarcodeStoreAction | null) =>
    (await call<{ data: BarcodeOverrideResult }>(`/staged-products/${stagedId}/barcode-override`, {
      method: 'POST',
      body: { expectedBefore, expectedAfter, storeAction },
    })).data,
  list: async (stagedId: string) =>
    (await call<{ data: BarcodeOverrideRecord[] }>(`/staged-products/${stagedId}/barcode-overrides`)).data,
  storeAction: async (stagedId: string, overrideId: string, action: BarcodeStoreAction) =>
    (await call<{ data: StoreActionSummary }>(`/staged-products/${stagedId}/barcode-overrides/${overrideId}/store-action`, {
      method: 'POST',
      body: { action },
    })).data,
};

/** One sentence per thing that happened, for the toast and the record. */
export function storeActionSentence(summary: StoreActionSummary | null): string {
  if (!summary) return 'No store listing carried the old barcode.';
  const parts: string[] = [];
  if (summary.pushed > 0) parts.push(`${summary.pushed} listing${summary.pushed === 1 ? '' : 's'} corrected`);
  if (summary.storesNotified > 0) parts.push(`${summary.storesNotified} store${summary.storesNotified === 1 ? '' : 's'} told`);
  if (summary.notCarrying > 0) parts.push(`${summary.notCarrying} no longer carried it`);
  if (summary.failed > 0) parts.push(`${summary.failed} failed`);
  if (summary.remaining > 0) parts.push(`${summary.remaining} not reached yet`);
  if (summary.storesSkippedFlagOff > 0) parts.push(`${summary.storesSkippedFlagOff} store${summary.storesSkippedFlagOff === 1 ? '' : 's'} skipped: the feature is off for them`);
  return parts.length > 0 ? `${parts.join(', ')}.` : 'Nothing left to do for the stores.';
}
